// Bookie · Dengiz Tubi — preview thumbnail. The full design lives at
// /templates/bookie-navy.html — CORPORATE 25-YEAR ANNIVERSARY occasion
// (navy #1F2A4A + paper #FBF6EC + gold #B89968, Bodoni Moda display,
// Cormorant Garamond italic body, Greek-key meander cornice — strictly
// geometric ornaments, no florals, no script). The catalog card echoes
// the hero: a small meander band on top, a tiny eyebrow, the big "25"
// numeral, the company name beneath, and footer date — stately,
// corporate-anniversary formality.

import type { LayoutCtx } from "./_shared";

export function BookieNavyPreviewLayout({ names, date, venue, ink, bg, font }: LayoutCtx) {
  // Corporate: names is a single company name. Drop any trailing
  // " & X" fragment if present.
  const company = (names || "").split(/\s*&\s*/)[0].trim();
  const navy = "#1F2A4A";
  const paper = "#FBF6EC";
  const paper2 = "#F2EADA";
  const gold = "#B89968";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${paper} 0%, ${paper2} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "10% 9% 9%",
      }}
    >
      {/* Greek-key (meander) cornice — strict geometry, gold on paper */}
      <svg
        aria-hidden
        viewBox="0 0 120 16"
        style={{ width: "78%", height: "auto", color: gold, opacity: 0.95 }}
      >
        <defs>
          <pattern id="thumb-meander" x="0" y="0" width="14" height="12" patternUnits="userSpaceOnUse">
            <path
              d="M0 10.5 L14 10.5 M1.5 10.5 L1.5 2.5 L10.5 2.5 L10.5 8 L5 8 L5 5 L8 5 L8 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.7"
              strokeLinejoin="miter"
              strokeLinecap="square"
            />
          </pattern>
        </defs>
        <line x1="0" y1="1" x2="120" y2="1" stroke="currentColor" strokeWidth="0.5" opacity="0.55" />
        <rect x="0" y="2" width="120" height="12" fill="url(#thumb-meander)" />
        <line x1="0" y1="15" x2="120" y2="15" stroke="currentColor" strokeWidth="0.5" opacity="0.55" />
      </svg>

      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.45em",
          textTransform: "uppercase",
          color: gold,
          marginTop: 12,
          fontWeight: 700,
        }}
      >
        Yubiley · 2001 — 2026
      </div>

      {/* Giant "25" numeral — Bodoni Moda */}
      <div
        style={{
          fontFamily: "'Bodoni Moda', serif",
          fontWeight: 500,
          fontSize: 78,
          lineHeight: 0.9,
          color: navy,
          marginTop: 6,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "lining-nums",
        }}
      >
        25
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 6.5,
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          color: gold,
          marginTop: 2,
          fontWeight: 600,
        }}
      >
        Yillik faoliyat
      </div>

      {/* Gold rule */}
      <div
        aria-hidden
        style={{
          width: 36,
          height: 1,
          background: `linear-gradient(to right, transparent, ${gold}, transparent)`,
          margin: "10px 0 8px",
        }}
      />

      {/* Company name — Bodoni Moda */}
      <div
        style={{
          fontFamily: "'Bodoni Moda', serif",
          fontWeight: 500,
          fontSize: 16,
          lineHeight: 1.1,
          color: navy,
          letterSpacing: "0.01em",
        }}
      >
        {company || names}
      </div>

      {/* Date + venue (footer) */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: navy,
          opacity: 0.78,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 10,
          color: navy,
          opacity: 0.72,
          marginTop: 2,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
